// SPDX-License-Identifier: AGPL-3.0-or-later

import * as NameplateCommands from '@app/features/user/commands/NameplateCommands';
import {decompressFrames, parseGIF, type ParsedFrame} from 'gifuct-js';

export interface DecodedNameplateGif {
	width: number;
	height: number;
	frames: ParsedFrame[];
}

const gifCache = new Map<string, Promise<DecodedNameplateGif | null>>();

function decodeGifBuffer(buffer: ArrayBuffer): DecodedNameplateGif | null {
	const parsed = parseGIF(buffer);
	const frames = decompressFrames(parsed, true);
	if (frames.length === 0) {
		return null;
	}
	return {
		width: parsed.lsd.width,
		height: parsed.lsd.height,
		frames,
	};
}

export function loadNameplateGif(src: string, catalogId?: string): Promise<DecodedNameplateGif | null> {
	const cacheKey = catalogId ? `id:${catalogId}` : src;
	const cached = gifCache.get(cacheKey);
	if (cached) {
		return cached;
	}
	const pending = (async () => {
		try {
			if (catalogId) {
				const proxied = await NameplateCommands.fetchNameplateMediaBytes(catalogId);
				if (proxied) {
					return decodeGifBuffer(proxied);
				}
			}
			const response = await fetch(src, {mode: 'cors', credentials: 'omit'});
			if (!response.ok) {
				return null;
			}
			return decodeGifBuffer(await response.arrayBuffer());
		} catch {
			return null;
		}
	})();
	gifCache.set(cacheKey, pending);
	return pending;
}

export function drawCoveredCenter(
	context: CanvasRenderingContext2D,
	source: CanvasImageSource,
	sourceWidth: number,
	sourceHeight: number,
	destWidth: number,
	destHeight: number,
): void {
	if (!sourceWidth || !sourceHeight || !destWidth || !destHeight) {
		return;
	}
	const sourceAspect = sourceWidth / sourceHeight;
	const destAspect = destWidth / destHeight;
	let sx = 0;
	let sy = 0;
	let sw = sourceWidth;
	let sh = sourceHeight;
	if (sourceAspect > destAspect) {
		sw = sourceHeight * destAspect;
		sx = (sourceWidth - sw) / 2;
	} else {
		sh = sourceWidth / destAspect;
		sy = (sourceHeight - sh) / 2;
	}
	context.clearRect(0, 0, destWidth, destHeight);
	context.drawImage(source, sx, sy, sw, sh, 0, 0, destWidth, destHeight);
}

export function composeGifFrame(
	gif: DecodedNameplateGif,
	frameIndex: number,
	composeCanvas: HTMLCanvasElement,
	previous?: ImageData | null,
): ImageData | null {
	const frame = gif.frames[frameIndex];
	if (!frame) {
		return previous ?? null;
	}
	if (composeCanvas.width !== gif.width || composeCanvas.height !== gif.height) {
		composeCanvas.width = gif.width;
		composeCanvas.height = gif.height;
	}
	const context = composeCanvas.getContext('2d');
	if (!context) {
		return previous ?? null;
	}
	if (frameIndex === 0) {
		context.clearRect(0, 0, gif.width, gif.height);
	} else if (frame.disposalType === 2) {
		const previousFrame = gif.frames[frameIndex - 1];
		if (previousFrame) {
			context.clearRect(
				previousFrame.dims.left,
				previousFrame.dims.top,
				previousFrame.dims.width,
				previousFrame.dims.height,
			);
		}
	} else if (frame.disposalType === 3 && previous) {
		context.putImageData(previous, 0, 0);
	}
	const imageData = context.createImageData(frame.dims.width, frame.dims.height);
	imageData.data.set(frame.patch);
	context.putImageData(imageData, frame.dims.left, frame.dims.top);
	try {
		return context.getImageData(0, 0, gif.width, gif.height);
	} catch {
		return null;
	}
}

export function frameDelayMs(frame: ParsedFrame | undefined): number {
	const delay = frame?.delay ?? 0;
	return delay < 20 ? 100 : delay;
}
